import React, { useEffect, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { translate } from 'react-jhipster';
import { toast } from 'react-toastify';
import { useAppSelector } from 'app/config/store';
import { AUTHORITIES } from 'app/config/constants';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';

interface IPendingCheckerRow {
  quotationId: number;
  referenceNumber?: string;
  customerName?: string;
  date?: string;
  amount?: number;
  status?: string;
  pending: boolean;
  locked: boolean;
}

interface PendingAmountCheckerProps {
  rebateSettingId: number | string;
  schedule: string;
  bankName: string;
}

/**
 * ALSHMOKH-70: lets an Admin/Sub-admin tick/untick "Pending amount" on the selected setting's
 * period, plus any older transaction still pending or waiting to be carried forward. A bank user
 * (or any other role able to reach this page, e.g. Auditor) must not see this section at all.
 */
export const PendingAmountChecker = ({ rebateSettingId, schedule, bankName }: PendingAmountCheckerProps) => {
  const account = useAppSelector(state => state.authentication.account);
  const canEdit = account?.authorities?.some(authority =>
    [AUTHORITIES.ADMIN, AUTHORITIES.ALSHMOKH_ADMIN, AUTHORITIES.SUBADMIN_REBATE_PENDING_CHECKER].includes(authority)
  );

  const [rows, setRows] = useState<IPendingCheckerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);

  const loadRows = async () => {
    if (!rebateSettingId || !canEdit) {
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post<IPendingCheckerRow[]>('api/rebate-documents/pending-checker', {
        rebateSettingId,
        schedule,
      });
      setRows(data);
    } catch (error) {
      toast.error(translate('comexApp.bank.rebate.pending.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [rebateSettingId, schedule, canEdit]);

  if (!canEdit) {
    return null;
  }

  const handleToggle = async (row: IPendingCheckerRow, checked: boolean) => {
    setSavingId(row.quotationId);
    try {
      await axios.patch(`api/rebate-documents/pending-checker/${row.quotationId}`, { pending: checked });
      setRows(prev => prev.map(r => (r.quotationId === row.quotationId ? { ...r, pending: checked } : r)));
    } catch (error) {
      const message =
        error?.response?.status === 400
          ? translate('comexApp.bank.rebate.pending.alreadyCounted')
          : translate('comexApp.bank.rebate.pending.saveError');
      toast.error(message);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {translate('comexApp.bank.rebate.pending.heading')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('comexApp.bank.rebate.pending.hint', { bankName })}
      </Typography>

      {loading ? (
        <CircularProgress size={20} />
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {translate('comexApp.bank.rebate.report.noTransactions')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{translate('comexApp.bank.rebate.report.columns.date')}</TableCell>
                <TableCell>{translate('comexApp.bank.rebate.report.columns.referenceNumber')}</TableCell>
                <TableCell>{translate('comexApp.bank.rebate.report.columns.customerName')}</TableCell>
                <TableCell align="right">{translate('comexApp.bank.rebate.report.columns.amount')}</TableCell>
                <TableCell>{translate('comexApp.bank.rebate.report.columns.status')}</TableCell>
                <TableCell align="center">{translate('comexApp.bank.rebate.pending.column')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.quotationId}>
                  <TableCell>{row.date ? dayjs(row.date).format('DD/MM/YYYY') : ''}</TableCell>
                  <TableCell>{row.referenceNumber}</TableCell>
                  <TableCell>{row.customerName}</TableCell>
                  <TableCell align="right">{row.amount}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={row.locked ? translate('comexApp.bank.rebate.pending.alreadyCounted') : ''}>
                      <span>
                        <Checkbox
                          size="small"
                          checked={row.pending}
                          disabled={savingId === row.quotationId || (row.locked && !row.pending)}
                          onChange={e => handleToggle(row, e.target.checked)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
